import {
    defineConnectorConstellationDerivation,
    defineConnectorItemDerivation,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toConnectorItemCollectionDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildLineGuideShape, buildPanelShape } from '@toolproof-core/visualization/_pure/shapes';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { CollectionKey } from '@toolproof-core/visualization/_contract/key';
import type {
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { deriveResourceTypeAngularInfo } from '@/workflow/_pure/derivation/resourceTypeAngularPlacement';
import { getResourceTypeRingRadius } from '@/workflow/_pure/config/derivationConfig';
import { RADIAL_PANEL_CLEARANCE } from '@/workflow-runner/_data/radialPanelGaps';
import type { Config } from '@/workflow/_contract/config';

export const MOCK_RESOURCE_TYPES = [
    { resourceTypeHandle: 'TYPE-MockAlpha', label: 'α', count: 1 },
    { resourceTypeHandle: 'TYPE-MockBeta', label: 'β', count: 0 },
    { resourceTypeHandle: 'TYPE-MockGamma', label: 'γ', count: 10 },
    { resourceTypeHandle: 'TYPE-MockDelta', label: 'δ', count: 1001 },
] as const;

const RESOURCE_EXAMPLES = [
    { resourceTypeHandle: 'TYPE-Natural', values: Array.from({ length: 1001 }, (_, index) => index) },
    { resourceTypeHandle: 'TYPE-Boolean', values: [true, false] },
] as const;

const MOCK_RESOURCE_EXAMPLES = [...RESOURCE_EXAMPLES, ...MOCK_RESOURCE_TYPES.map(({ resourceTypeHandle, label, count }) => ({
    resourceTypeHandle,
    values: Array.from({ length: count }, (_, index) => `${label}${index}`),
}))];

export function withConceptResourceTypeConfig(config: Config): Config {
    return { ...config, domainPresentation: { ...config.domainPresentation,
        entities: { ...config.domainPresentation.entities,
            resourceTypes: { ...config.domainPresentation.entities.resourceTypes,
                angularPlacement: { placementKind: 'evenly-spaced', startAngle: Math.PI } } } } };
}

function resourceCollectionKey(resourceTypeHandle: string): CollectionKey<'entity'> {
    return `collection:entity:Resource:${resourceTypeHandle}`;
}

export const CONCEPT_RESOURCE_RADIAL_COLLECTION_KEY = 'collection:connector:ConceptResourceRadial';

export function withConceptResourceRadials(config: Config, collections: EntityItemCollectionDerivation[]) {
    const guide = config.domainPresentation.connectors.resourceTypeRadial;
    if (!guide.enabled) return collections;
    const ringRadius = getResourceTypeRingRadius(config);
    const sphereRadius = config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
    const itemDerivations = collections.filter(collection =>
        collection.itemCollectionKey.startsWith('collection:entity:Resource:')).flatMap(collection => {
        const last = collection.itemDerivations.at(-1);
        if (!last) return [];
        const shape = last.parametricState?.shape;
        if (shape?.shapeKind !== 'panel' || shape.curvatureRadius === undefined) {
            throw new Error(`Concept Resource radial requires a curved panel in ${collection.itemCollectionKey}.`);
        }
        const handle = collection.itemCollectionKey.slice('collection:entity:Resource:'.length);
        const ringItemKey = `item:entity:${handle}` as const;
        const endpoint = {
            endpointKind: 'parametric', parametricKind: 'ring-intersection', ringItemKey,
            anglePolicy: { anglePolicyKind: 'match-item-in-ring', matchItemKey: ringItemKey },
        } as const;
        return [defineConnectorItemDerivation({
            item: { itemKind: 'connector', itemKey: `item:connector:ConceptResourceRadial:${handle}`,
                shape: buildLineGuideShape(),
                endpoints: { from: { ...endpoint, radialOffset: sphereRadius },
                    to: { ...endpoint, radialOffset: shape.curvatureRadius - ringRadius } } },
            parametricState: { ...guide, visualStyle: { ...guide.visualStyle, opacity: 0.15 }, lineGaps: {
                itemKeys: collection.itemDerivations.map(derivation => derivation.item.itemKey),
                clearance: RADIAL_PANEL_CLEARANCE,
            } },
        })];
    });
    return [...collections, toConnectorItemCollectionDerivation({ itemCollectionKey: CONCEPT_RESOURCE_RADIAL_COLLECTION_KEY, itemDerivations })];
}

export function deriveConceptResourceRadialConstellations(itemCollections: ItemCollection[]) {
    return itemCollections.some(collection => collection.itemCollectionKey === CONCEPT_RESOURCE_RADIAL_COLLECTION_KEY)
        ? [defineConnectorConstellationDerivation({
            constellation: { constellationKind: 'connector', constellationKey: 'constellation:connector:ConceptResourceRadial',
                itemCollectionKey: CONCEPT_RESOURCE_RADIAL_COLLECTION_KEY },
        })] : [];
}

export function deriveConceptResourceCollections(config: Config, mock = false): EntityItemCollectionDerivation[] {
    const panel = config.domainPresentation.entities.workflowStateEntries.item;
    const sphereRadius = config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
    const startRadius = getResourceTypeRingRadius(config) + sphereRadius + panel.shape.height * 3;
    return (mock ? MOCK_RESOURCE_EXAMPLES : RESOURCE_EXAMPLES).map(({ resourceTypeHandle, values }) => toEntityItemCollectionDerivation({
        itemCollectionKey: resourceCollectionKey(resourceTypeHandle),
        itemDerivations: values.map((value, index) => defineEntityItemDerivation({
            item: {
                itemKind: 'entity',
                itemKey: `item:entity:Resource:${resourceTypeHandle}:${value}`,
                name: String(value),
                description: `Resource instance of ${resourceTypeHandle}: ${value}`,
            },
            parametricState: {
                shape: { ...buildPanelShape({ width: panel.shape.width, height: panel.shape.height,
                    depth: panel.shape.depth, curvatureRadius: startRadius + index * panel.shape.height * 3 }),
                    appearanceMode: 'filled' },
                visualStyle: config.domainPresentation.entities.resourceTypes.item?.visualStyle,
                label: { onItem: { ...panel.label?.onItem, text: String(value) } },
            },
        })),
    }));
}

export function deriveConceptResourceConstellations(args: {
    itemCollections: ItemCollection[];
    config: Config;
    mock?: boolean;
}): EntityConstellationDerivation[] {
    const { angleByHandle } = deriveResourceTypeAngularInfo(args);
    const panel = args.config.domainPresentation.entities.workflowStateEntries.item.shape;
    const sphereRadius = args.config.domainPresentation.entities.resourceTypes.item?.shape?.radius ?? 0;
    const spacing = panel.height * 3;
    return (args.mock ? MOCK_RESOURCE_EXAMPLES : RESOURCE_EXAMPLES).filter(example => example.values.length > 0).map(({ resourceTypeHandle }) => {
        const collection = args.itemCollections.find(collection =>
            collection.itemCollectionKey === resourceCollectionKey(resourceTypeHandle));
        const firstItem = collection?.items[0];
        const angle = angleByHandle.get(resourceTypeHandle);
        if (!firstItem || firstItem.itemKind !== 'entity' || angle === undefined) {
            throw new Error(`Concept Resource radial requires ${resourceTypeHandle} and its Resource examples.`);
        }
        return defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: `constellation:entity:Resource:${resourceTypeHandle}`,
                itemCollectionKey: resourceCollectionKey(resourceTypeHandle),
                anchor: { itemKey: firstItem.itemKey, anchorKey: 'center' },
            },
            parametricState: {
                layout: {
                    layoutKind: 'spoke',
                    anchor: {
                        anchorKind: 'parametric',
                        parametricKind: 'ring-intersection',
                        ringItemKey: `item:entity:${resourceTypeHandle}`,
                        anglePolicy: { anglePolicyKind: 'match-item-in-ring', matchItemKey: `item:entity:${resourceTypeHandle}` },
                        radialOffset: sphereRadius + spacing,
                    },
                    direction: [Math.cos(angle), 0, Math.sin(angle)],
                    spacing,
                },
            },
        });
    });
}
